import { useCallback, useEffect, useRef, useState } from "react";
import {
  type AuditEntry,
  type Bucket,
  type PhotoKind,
  type Project,
  type ProjectDetail as Detail,
  type Status,
} from "../../shared/types.ts";
import { api, photoUrl } from "../api.ts";
import {
  AreaBadge,
  Banner,
  BucketPicker,
  Loading,
  Markdown,
  StatusPicker,
  describeAuditEntry,
  formatDate,
  formatRelative,
} from "../components.tsx";
import { navigate } from "../useRoute.ts";

export function ProjectDetail({ id }: { id: string }) {
  const [project, setProject] = useState<Detail | null>(null);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nextProject, nextHistory] = await Promise.all([api.getProject(id), api.auditLog(id)]);
      setProject(nextProject);
      setHistory(nextHistory);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load project");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(changes: Parameters<typeof api.updateProject>[1]) {
    setBusy(true);
    setError(null);
    try {
      await api.updateProject(id, changes);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (error && !project) return <Banner kind="error">{error}</Banner>;
  if (!project) return <Loading />;

  return (
    <div className="detail">
      {error && <Banner kind="error">{error}</Banner>}

      <TitleEditor project={project} onSave={(title) => patch({ title })} busy={busy} />

      <div className="detail-controls">
        <div className="control-group">
          <span className="control-label">Status</span>
          <StatusPicker
            value={project.status}
            disabled={busy}
            onChange={(status: Status) => patch({ status })}
          />
        </div>

        <div className="control-group">
          <span className="control-label">Bucket</span>
          <BucketPicker
            value={project.bucket}
            disabled={busy}
            onChange={(bucket: Bucket) => patch({ bucket })}
          />
        </div>

        <label>
          Area
          <input
            defaultValue={project.area}
            placeholder="yard, garage…"
            disabled={busy}
            onBlur={(e) => e.target.value !== project.area && patch({ area: e.target.value })}
          />
        </label>
      </div>

      <p className="muted small">
        Added by {project.created_by_name} on {formatDate(project.created_at)}
        {project.completed_at && ` · finished ${formatDate(project.completed_at)}`}
      </p>

      <Photos project={project} onChange={load} onError={setError} />
      <Notes project={project} onSave={(notes_md) => patch({ notes_md })} busy={busy} />
      <Dependencies project={project} onChange={load} onError={setError} />
      <Comments project={project} onChange={load} onError={setError} />
      <History entries={history} />
    </div>
  );
}

/**
 * Compact, newest-first log of who changed what. Collapsed behind a toggle
 * once it grows past a handful of entries — mostly for mobile, where a long
 * history would otherwise push everything else off screen.
 */
function History({ entries }: { entries: AuditEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const COLLAPSE_AFTER = 5;
  const collapsible = entries.length > COLLAPSE_AFTER;
  const showEntries = !collapsible || expanded;

  return (
    <section className="card">
      <div className="card-header">
        {collapsible ? (
          <button className="link history-toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Hide history" : `History (${entries.length})`}
          </button>
        ) : (
          <h3>History</h3>
        )}
      </div>

      {entries.length === 0 && <p className="muted">No history yet.</p>}

      {showEntries && entries.length > 0 && (
        <ul className="plain audit-log">
          {entries.map((entry) => (
            <li key={entry.id} className="audit-entry">
              {describeAuditEntry(entry)} <span className="muted small">· {formatRelative(entry.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TitleEditor({
  project,
  onSave,
  busy,
}: {
  project: Detail;
  onSave: (title: string) => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.title);

  useEffect(() => setValue(project.title), [project.title]);

  if (!editing) {
    return (
      <h2 className="detail-title" onClick={() => setEditing(true)}>
        {project.title} <span className="edit-hint">✎</span>
      </h2>
    );
  }

  return (
    <div className="row">
      <input value={value} onChange={(e) => setValue(e.target.value)} autoFocus disabled={busy} />
      <button
        className="primary"
        onClick={() => {
          if (value.trim() && value !== project.title) onSave(value.trim());
          setEditing(false);
        }}
      >
        Save
      </button>
      <button
        className="secondary"
        onClick={() => {
          setValue(project.title);
          setEditing(false);
        }}
      >
        Cancel
      </button>
    </div>
  );
}

function Photos({
  project,
  onChange,
  onError,
}: {
  project: Detail;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [kind, setKind] = useState<PhotoKind>("normal");
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setUploading(true);
    try {
      await api.uploadPhoto(project.id, file, kind);
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <section className="card">
      <h3>Photos</h3>

      {project.photos.length > 0 ? (
        <div className="gallery">
          {project.photos.map((photo) => (
            <a key={photo.id} href={photoUrl(photo.id, "display")} target="_blank" rel="noreferrer">
              <img src={photoUrl(photo.id, "thumb")} alt={photo.kind} loading="lazy" />
              {photo.kind !== "normal" && <span className="photo-kind">{photo.kind}</span>}
            </a>
          ))}
        </div>
      ) : (
        <p className="muted">No photos yet.</p>
      )}

      <div className="row wrap">
        <select value={kind} onChange={(e) => setKind(e.target.value as PhotoKind)}>
          <option value="normal">Photo</option>
          <option value="before">Before</option>
          <option value="after">After</option>
        </select>
        <input
          ref={input}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
          }}
        />
        <button className="secondary" disabled={uploading} onClick={() => input.current?.click()}>
          {uploading ? "Uploading…" : "📷 Add photo"}
        </button>
      </div>
    </section>
  );
}

function Notes({
  project,
  onSave,
  busy,
}: {
  project: Detail;
  onSave: (notes: string) => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.notes_md);

  useEffect(() => setValue(project.notes_md), [project.notes_md]);

  return (
    <section className="card">
      <div className="card-header">
        <h3>Notes</h3>
        <button className="link" onClick={() => setEditing(!editing)}>
          {editing ? "Preview" : "Edit"}
        </button>
      </div>

      {editing ? (
        <>
          <textarea
            value={value}
            rows={10}
            onChange={(e) => setValue(e.target.value)}
            placeholder={"Markdown welcome.\n\n## Shopping list\n- [ ] 2x4s\n- [ ] deck screws"}
          />
          <div className="row">
            <button
              className="primary"
              disabled={busy}
              onClick={() => {
                onSave(value);
                setEditing(false);
              }}
            >
              Save notes
            </button>
            <button
              className="secondary"
              onClick={() => {
                setValue(project.notes_md);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <Markdown source={project.notes_md} />
      )}
    </section>
  );
}

function Dependencies({
  project,
  onChange,
  onError,
}: {
  project: Detail;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [candidates, setCandidates] = useState<Project[]>([]);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listProjects({ includeDone: true })
      .then((all) => setCandidates(all.filter((p) => p.id !== project.id)))
      .catch(() => setCandidates([]));
  }, [project.id]);

  const blockerIds = new Set(project.blocked_by.map((b) => b.id));
  const options = candidates.filter((p) => !blockerIds.has(p.id));

  async function add() {
    if (!choice) return;
    setBusy(true);
    try {
      await api.addDependency(project.id, choice);
      setChoice("");
      await onChange();
    } catch (err) {
      // The server rejects anything that would create a cycle.
      onError(err instanceof Error ? err.message : "Could not add dependency");
    } finally {
      setBusy(false);
    }
  }

  async function remove(blockerId: string) {
    setBusy(true);
    try {
      await api.removeDependency(project.id, blockerId);
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not remove dependency");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Blocked by</h3>

      {project.blocked_by.length > 0 ? (
        <ul className="plain">
          {project.blocked_by.map((blocker) => (
            <li key={blocker.id} className="row between">
              <button className="link" onClick={() => navigate(`/p/${blocker.id}`)}>
                {blocker.status === "done" ? "✅" : "⛔"} {blocker.title}
              </button>
              <button className="link danger" disabled={busy} onClick={() => remove(blocker.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Nothing is blocking this.</p>
      )}

      <div className="row wrap">
        <select value={choice} onChange={(e) => setChoice(e.target.value)}>
          <option value="">Add a blocker…</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.title}
            </option>
          ))}
        </select>
        <button className="secondary" disabled={!choice || busy} onClick={add}>
          Add
        </button>
      </div>

      {project.blocks.length > 0 && (
        <p className="muted small">
          Blocks: {project.blocks.map((b) => b.title).join(", ")}
        </p>
      )}
    </section>
  );
}

function Comments({
  project,
  onChange,
  onError,
}: {
  project: Detail;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.addComment(project.id, body.trim());
      setBody("");
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not add comment");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Comments</h3>

      {project.comments.map((comment) => (
        <article key={comment.id} className="comment">
          <header>
            <strong>{comment.person_name}</strong> <span className="muted small">{formatDate(comment.created_at)}</span>
          </header>
          <Markdown source={comment.body_md} />
        </article>
      ))}

      {project.comments.length === 0 && <p className="muted">No comments yet.</p>}

      <form onSubmit={add}>
        <textarea
          value={body}
          rows={3}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment…"
        />
        <button type="submit" className="primary" disabled={busy || !body.trim()}>
          Post
        </button>
      </form>
    </section>
  );
}
